import { FlexColumn, YellowText as TextYellow } from '@elements/Common';
import { brand } from '@utility/config';
import { Flag } from '@elements/Flag';
import { DarkPopup, LightPopup } from '@elements/Popup';
import { DateUtility } from '@utility/date';
import { Link } from 'react-router';
import { Circle, Info } from '@phosphor-icons/react';
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import { differenceInDays } from '@page-components/timeline/common';
import { DatesBar } from '@page-components/timeline';

type DivProps = ComponentPropsWithoutRef<'div'>;
type SpanProps = ComponentPropsWithoutRef<'span'>;

function YellowText({ className, ...props }: SpanProps) {
  return (
    <TextYellow className={cn('sk:cursor-pointer', className)} {...props} />
  );
}

const px = (value?: number) => (value === undefined ? undefined : `${value}px`);

function BarContainer({
  width,
  children,
}: {
  width: number;
  children: ReactNode;
}) {
  return (
    <div
      className="sk:relative sk:z-[2] sk:max-h-10 sk:min-h-10"
      style={{ minWidth: px(width), maxWidth: px(width) }}
    >
      {children}
    </div>
  );
}

/** The bar colours come from the chart data, so they stay inline. */
function Bar({
  left,
  width,
  bgColor,
  children,
}: {
  left?: number;
  width?: number;
  bgColor?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className="sk:group/bar sk:absolute sk:flex sk:max-h-[30px] sk:min-h-[30px] sk:items-center sk:justify-between sk:rounded-[3px]"
      style={{
        background: bgColor,
        minWidth: px(width),
        maxWidth: px(width),
        left: px(left),
      }}
    >
      {children}
    </div>
  );
}

export function Chart({ chartData = [], startDate, endDate, context }) {
  const DayWidth = 30;

  const chartRef = useRef(null);
  const [scroll, setCurrentScroll] = useState(0);

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const mouseOnSlider = e => {
    setIsDown(true);
    if (!chartRef.current) return;
    // @ts-expect-error - TS2339: Property 'offsetLeft' does not exist on type 'never'.
    setStartX(e.pageX - chartRef.current.offsetLeft);
    // @ts-expect-error - TS2339: Property 'scrollLeft' does not exist on type 'never'.
    setScrollLeft(chartRef.current.scrollLeft);
  };

  const notSliding = () => {
    if (!chartRef.current) return;
    setIsDown(false);
  };

  const moveSlider = e => {
    if (!isDown || !chartRef.current) return;
    e.preventDefault();
    // @ts-expect-error - TS2339: Property 'offsetLeft' does not exist on type 'never'.
    const x = e.pageX - chartRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    // @ts-expect-error - TS2339: Property 'scrollLeft' does not exist on type 'never'.
    chartRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div
      ref={chartRef}
      onMouseMove={moveSlider}
      onMouseLeave={notSliding}
      onMouseUp={notSliding}
      onMouseDown={mouseOnSlider}
      className={cn(
        'sk:relative sk:w-full sk:overflow-x-scroll sk:rounded-[5px] sk:bg-page sk:p-5 sk:pb-10 sk:pl-[22px] sk:select-none',
        isDown ? 'sk:cursor-grabbing' : 'sk:cursor-grab',
      )}
      onScroll={() => {
        // @ts-expect-error - TS18047: 'chartRef.current' is possibly 'null'.
        if (chartRef) setCurrentScroll(chartRef.current.scrollLeft);
      }}
    >
      {/* @ts-expect-error - ts-migration */}
      <DatesBar startDate={startDate} endDate={endDate} DayWidth={DayWidth} />
      {chartData.map(bar => (
        <BarRenderer
          data={bar}
          startDate={startDate}
          endDate={endDate}
          DayWidth={DayWidth}
          context={context}
          scroll={scroll}
        />
      ))}
    </div>
  );
}

function BarRenderer({ data, startDate, endDate, DayWidth, scroll, context }) {
  const nameSpaceFromLeft = (sDate, eDate) => {
    if (differenceInDays(sDate, eDate) < 7)
      return differenceInDays(startDate, eDate) * DayWidth;
    return differenceInDays(startDate, sDate) * DayWidth;
  };

  return (
    <BarContainer width={DateUtility.difference(startDate, endDate) * DayWidth}>
      <div
        className="sk:absolute sk:left-2.5 sk:mt-[3px]"
        style={{
          minWidth: px(
            DateUtility.difference(startDate, data.startDate) * DayWidth - 20,
          ),
          maxWidth: px(
            DateUtility.difference(startDate, data.startDate) * DayWidth - 20,
          ),
        }}
      >
        <span className="sk:sticky sk:left-0 sk:text-[15px]">
          {data.startText}
        </span>
      </div>
      <Bar
        left={differenceInDays(startDate, data.startDate) * DayWidth}
        bgColor={data.color}
        width={differenceInDays(data.startDate, data.endDate) * DayWidth}
      >
        {data.siblings?.length > 0 ? (
          <NameRenderer
            DayWidth={DayWidth}
            parent={data.siblings?.length > 0}
            data={data}
            scroll={scroll}
            startDate={startDate}
            context={context}
          />
        ) : (
          <></>
        )}
      </Bar>
      {data.siblings?.length > 0 ? (
        data.siblings?.map((bar, i) => (
          <>
            <Bar
              left={differenceInDays(startDate, bar.startDate) * DayWidth}
              bgColor={bar.color}
              width={differenceInDays(bar.startDate, bar.endDate) * DayWidth}
            >
              {i + 1 === data.siblings.length ? (
                <></>
              ) : (
                // @ts-expect-error - TS2741: Property 'parent' is missing in type '{ context: any; DayWidth: any; scroll: any...
                <NameRenderer
                  context={context}
                  DayWidth={DayWidth}
                  scroll={scroll}
                  data={bar}
                  startDate={startDate}
                />
              )}
            </Bar>
            {i + 1 === data.siblings.length ? (
              <Bar
                left={nameSpaceFromLeft(bar.startDate, bar.endDate)}
                width={differenceInDays(bar.startDate, endDate) * DayWidth}
              >
                <NameRenderer
                  DayWidth={DayWidth}
                  scroll={scroll}
                  startDate={startDate}
                  data={bar}
                  context={context}
                  // @ts-expect-error - TS2322: Type 'string | null' is not assignable to type 'string | undefined'.
                  textColor={
                    differenceInDays(bar.startDate, bar.endDate) < 7
                      ? 'black'
                      : null
                  }
                />
              </Bar>
            ) : (
              <></>
            )}
          </>
        ))
      ) : (
        <Bar
          left={nameSpaceFromLeft(data.startDate, data.endDate)}
          width={differenceInDays(data.startDate, endDate) * DayWidth}
        >
          <NameRenderer
            context={context}
            // @ts-expect-error - TS2322: Type 'string | null' is not assignable to type 'string | undefined'.
            textColor={
              differenceInDays(data.startDate, data.endDate) < 7
                ? 'black'
                : null
            }
            DayWidth={DayWidth}
            startDate={startDate}
            scroll={scroll}
            data={data}
          />
        </Bar>
      )}
    </BarContainer>
  );
}

const NAME =
  'sk:sticky sk:left-0 sk:flex sk:items-center sk:gap-x-2.5 sk:px-[5px] sk:py-0 sk:[&_span]:whitespace-nowrap';

/** Label colours follow the bar they sit on, which the data decides. */
function Name({
  textColor,
  children,
}: {
  textColor?: string;
  children: ReactNode;
}) {
  return (
    <div className={NAME} style={{ color: textColor }}>
      {children}
    </div>
  );
}

function AnchorName({
  textColor,
  className,
  style,
  ...props
}: ComponentPropsWithoutRef<'a'> & { textColor?: string }) {
  return (
    <a
      className={cn(NAME, 'sk:hover:underline', className)}
      style={{ color: textColor, ...style }}
      {...props}
    />
  );
}

const HIDDEN_NAME =
  'sk:group/name sk:[&_span]:inline-block sk:[&_span]:max-w-(--name-width) sk:[&_span]:whitespace-nowrap';

function HiddenName({
  width,
  textColor,
  children,
}: {
  width: number;
  textColor?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(NAME, HIDDEN_NAME)}
      style={{ color: textColor, '--name-width': px(width) } as CSSProperties}
    >
      {children}
    </div>
  );
}

function AnchorHiddenName({
  width,
  textColor,
  ...props
}: ComponentPropsWithoutRef<'a'> & { width: number; textColor?: string }) {
  return (
    <a
      className={cn(NAME, HIDDEN_NAME, 'sk:mr-[5px] sk:hover:underline')}
      style={{ color: textColor, '--name-width': px(width) } as CSSProperties}
      {...props}
    />
  );
}

/** Its width is capped by the --name-width its HiddenName parent sets. */
const OverflowContainer = forwardRef<HTMLSpanElement, SpanProps>(
  (props, ref) => (
    <span
      ref={ref}
      className="sk:inline-block sk:items-center sk:overflow-hidden! sk:text-ellipsis sk:whitespace-nowrap"
      {...props}
    />
  ),
);
OverflowContainer.displayName = 'OverflowContainer';

const FLOATING =
  'sk:absolute sk:-mt-[62px] sk:rounded-[5px] sk:bg-surface/70 sk:px-2.5 sk:py-[5px] sk:whitespace-nowrap';

function FullText({ children }: { children: ReactNode }) {
  return (
    <div className={cn(FLOATING, 'sk:hidden sk:group-hover/name:block')}>
      {children}
    </div>
  );
}

function InfoIcon() {
  return <Info size={18} className="sk:min-w-[18px]" />;
}

function Hint({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        FLOATING,
        'sk:hidden sk:text-fg sk:capitalize sk:shadow-card sk:backdrop-blur-[2px] sk:group-hover/bar:block',
      )}
    >
      {children}
    </div>
  );
}

function ShortName({ data, DayWidth, isPO }) {
  const [elementRect, setElementRect] = useState();

  const isOverflown = useMemo(() => {
    if (!elementRect) return false;

    const element = elementRect;
    return (
      // @ts-expect-error - TS2339: Property 'scrollHeight' does not exist on type 'never'.
      element.scrollHeight > element.clientHeight ||
      // @ts-expect-error - TS2339: Property 'scrollWidth' does not exist on type 'never'.
      element.scrollWidth > element.clientWidth
    );
  }, [elementRect, data]);

  const handleRect = useCallback(node => {
    setElementRect(node);
  }, []);

  const nameWidth = useMemo(
    () => differenceInDays(data.startDate, data.endDate) * DayWidth - 60,
    [data],
  );

  return data.link ? (
    <AnchorHiddenName
      textColor={data.textColor || 'black'}
      target="_blank"
      href={data.link}
      width={nameWidth > 0 ? nameWidth : 10}
    >
      {isPO ? (
        data.region ? (
          <Flag name={data.region} />
        ) : (
          <></>
        )
      ) : (
        <LeftIcon
          icon_type={data.icon_type}
          color={data.textColor || 'black'}
        />
      )}
      <OverflowContainer ref={handleRect}>
        {nameWidth > -8 ? (
          <>
            {data.name}&nbsp;&nbsp;&nbsp;{data.daysLeft}D
          </>
        ) : (
          '...'
        )}
      </OverflowContainer>
      {isOverflown || nameWidth <= -8 ? (
        <FullText>
          {data.name}&nbsp;&nbsp;&nbsp;{data.daysLeft}D{' '}
          {data.hint && ` - ${data.hint}`}
        </FullText>
      ) : (
        data.hint && <Hint>{data.hint}</Hint>
      )}
      {isValidElement(data.popupContent) && (
        <DarkPopup
          trigger={<InfoIcon />}
          hoverable
          contentWidth={500}
          position="top center"
        >
          {cloneElement(data.popupContent)}
        </DarkPopup>
      )}
    </AnchorHiddenName>
  ) : (
    <HiddenName
      textColor={data.textColor || 'black'}
      width={nameWidth > 0 ? nameWidth : 10}
    >
      {isPO ? (
        data.region ? (
          <Flag name={data.region} />
        ) : (
          <></>
        )
      ) : (
        <LeftIcon
          icon_type={data.icon_type}
          color={data.textColor || 'black'}
        />
      )}
      <OverflowContainer ref={handleRect}>
        {nameWidth > -8 ? (
          <>
            {data.name}&nbsp;&nbsp;&nbsp;{data.daysLeft}D
          </>
        ) : (
          '...'
        )}
      </OverflowContainer>
      {(isOverflown || nameWidth <= -8) && (
        <FullText>
          {data.name}&nbsp;&nbsp;&nbsp;{data.daysLeft}D
        </FullText>
      )}
      {isValidElement(data.popupContent) && (
        <DarkPopup
          trigger={<InfoIcon />}
          hoverable
          contentWidth={500}
          position="top center"
        >
          {cloneElement(data.popupContent)}
        </DarkPopup>
      )}
    </HiddenName>
  );
}

function NameRenderer({
  data,
  parent,
  DayWidth,
  scroll,
  startDate,
  textColor = '',
  context,
}) {
  const [passed, setPassed] = useState(false);
  const posOnX = useMemo(
    () => differenceInDays(startDate, data.endDate) * DayWidth,
    [data, startDate],
  );

  useEffect(() => {
    if (posOnX < scroll) {
      setPassed(true);
    } else {
      setPassed(false);
    }
  }, [scroll]);

  const isPO = useMemo(() => context === 'PO', [context]);

  const color = useMemo(
    () => textColor || (passed ? 'black' : 'white'),
    [passed, textColor],
  );

  if (data.link) {
    return (
      <>
        {parent ? (
          <ShortName isPO={isPO} DayWidth={DayWidth} data={data} />
        ) : (
          <Link to={data.link}>
            <AnchorName target="_blank" textColor={color}>
              {data.hint && <Hint>{data.hint}</Hint>}
              {isPO ? (
                data.region ? (
                  <Flag name={data.region} />
                ) : (
                  <></>
                )
              ) : (
                <LeftIcon icon_type={data.icon_type} color={color} />
              )}
              <span>{data.name}</span>
              {isValidElement(data.popupContent) && (
                <DarkPopup
                  trigger={<Info size={18} />}
                  hoverable
                  contentWidth={750}
                  position="top center"
                >
                  {cloneElement(data.popupContent)}
                </DarkPopup>
              )}
              <span>{data.daysLeft}D</span>
            </AnchorName>
          </Link>
        )}
      </>
    );
  }

  return (
    <>
      {parent ? (
        <ShortName isPO={isPO} DayWidth={DayWidth} data={data} />
      ) : (
        <Name textColor={color}>
          {isValidElement(data.customRender) ? (
            <>
              {cloneElement(data.customRender)}
              {isValidElement(data.popupContent) && (
                <DarkPopup
                  trigger={<Info size={18} />}
                  hoverable
                  contentWidth={750}
                  position="top center"
                >
                  {cloneElement(data.popupContent)}
                </DarkPopup>
              )}
            </>
          ) : (
            <>
              {isPO ? (
                data.region ? (
                  <Flag name={data.region} />
                ) : (
                  <></>
                )
              ) : (
                <LeftIcon icon_type={data.icon_type} color={color} />
              )}
              <span>{data.name}</span>
              {data.hint && <Hint>{data.hint}</Hint>}
              {isValidElement(data.popupContent) && (
                <DarkPopup
                  trigger={<Info size={18} />}
                  hoverable
                  contentWidth={750}
                  position="top center"
                >
                  {cloneElement(data.popupContent)}
                </DarkPopup>
              )}
              <span>{data.daysLeft}D</span>
            </>
          )}
        </Name>
      )}
    </>
  );
}

const types = {
  shipment: 'shipment',
  provider: 'provider',
  warehouse: 'warehouse',
  truck: 'truck',
};

function LeftIcon({ icon_type, color }) {
  if (icon_type === types.warehouse) {
    return <StorageIcon color={color} />;
  }
  if (icon_type === types.truck) {
    return <VanIcon color={color} />;
  }
  // @ts-expect-error - TS2322: Type '{ bgColor: any; }' is not assignable to type 'IntrinsicAttributes & IconPr...
  return <Circle bgColor={color} />;
}

function StorageIcon({ color }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.904097 4.29498C0.657066 4.45461 0.508301 4.72582 0.508301 5.01657V14.1173C0.508301 14.5941 0.901225 14.9806 1.38592 14.9806H2.14116C2.69344 14.9806 3.14116 14.5329 3.14116 13.9806V7.64227C3.14116 7.16546 3.53408 6.77893 4.01878 6.77893H11.4785C11.9632 6.77893 12.3562 7.16546 12.3562 7.64227V13.9806C12.3562 14.5329 12.8039 14.9806 13.3562 14.9806H14.1114C14.5961 14.9806 14.989 14.5941 14.989 14.1173V5.01657C14.989 4.72583 14.8403 4.45461 14.5932 4.29498L8.23049 0.641746C7.938 0.452751 7.55933 0.452751 7.26684 0.641747L0.904097 4.29498Z"
        fill={color}
      />
      <rect
        x="8.40796"
        y="11.959"
        width="3.07167"
        height="3.02167"
        rx="1.51083"
        fill={color}
      />
      <rect
        x="8.40796"
        y="11.959"
        width="3.07167"
        height="3.02167"
        rx="1"
        fill={color}
      />
      <rect
        x="4.89526"
        y="11.959"
        width="3.07167"
        height="3.02167"
        rx="1"
        fill={color}
      />
      <rect
        x="8.40796"
        y="8.50586"
        width="3.07167"
        height="3.02167"
        rx="1"
        fill={color}
      />
    </svg>
  );
}

function VanIcon({ color }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 6C9 5.44772 9.44772 5 10 5H11.5858C11.851 5 12.1054 5.10536 12.2929 5.29289L14.7071 7.70711C14.8946 7.89464 15 8.149 15 8.41421V10C15 10.5523 14.5523 11 14 11H10C9.44772 11 9 10.5523 9 10V6Z"
        fill={color}
      />
      <path
        d="M0 4C0 3.44772 0.447715 3 1 3H7C7.55229 3 8 3.44772 8 4V10C8 10.5523 7.55229 11 7 11H1C0.447715 11 0 10.5523 0 10V4Z"
        fill={color}
      />
      <circle cx="3" cy="11" r="1.5" fill={color} />
      <circle cx="11.5" cy="11" r="1.5" fill={color} />
    </svg>
  );
}
function SpacedRow({ className, ...props }: DivProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:w-full sk:min-w-[300px] sk:items-center sk:justify-between sk:gap-x-[2em] sk:[&_p]:m-0 sk:[&_p]:w-fit',
        className,
      )}
      {...props}
    />
  );
}

export function ShipmentPopupContent({ poShipment, buffer, data }) {
  const { transit_time, warehouse_outbound_buffer, inbound_buffer } = buffer;
  const total =
    (transit_time || 0) +
    (warehouse_outbound_buffer || 0) +
    (inbound_buffer || 0);

  if (
    !transit_time &&
    !warehouse_outbound_buffer &&
    !inbound_buffer &&
    !poShipment
  )
    return <></>;
  return (
    <div>
      <h3 className="sk:mt-[5px] sk:mb-5 sk:text-inherit">
        Leadtime Parameters
      </h3>
      {data?.daysLeft ? (
        <SpacedRow className="sk:mb-2!">
          Shipping Buffer {data.daysLeft}d (Prediction)
        </SpacedRow>
      ) : (
        <></>
      )}
      {warehouse_outbound_buffer ? (
        <SpacedRow className="sk:mb-2!">
          <p>Preparation Time</p>
          <span>
            {warehouse_outbound_buffer} Days (
            {Math.round((warehouse_outbound_buffer / total) * 100)}%)
          </span>
        </SpacedRow>
      ) : (
        <></>
      )}
      {transit_time ? (
        <SpacedRow className="sk:mb-2!">
          <p>Transit</p>
          <span>
            {transit_time} Days ({Math.round((transit_time / total) * 100)}%)
          </span>
        </SpacedRow>
      ) : (
        <></>
      )}
      {inbound_buffer ? (
        <SpacedRow>
          <p>Inbound Time</p>
          <span>
            {inbound_buffer} Days ({Math.round((inbound_buffer / total) * 100)}
            %)
          </span>
        </SpacedRow>
      ) : (
        <></>
      )}
    </div>
  );
}

/* Drawn inside DarkPopup, whose bubble already sets white text. */
function EquationContainer({ className, ...props }: DivProps) {
  return (
    <div
      className={cn(
        'sk:mr-[1em] sk:flex sk:min-w-[300px] sk:items-end sk:gap-x-[0.5em]',
        'sk:[&_p]:text-center sk:[&_p]:font-thin sk:[&_p]:whitespace-nowrap',
        'sk:[&_span]:font-semibold sk:[&_span]:whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}

function MarginLessFlexColumns({ children }: { children: ReactNode }) {
  return <FlexColumn className="sk:-mx-0.5">{children}</FlexColumn>;
}

export function ProviderPopupContent({
  minimumBuffer,
  leadTime,
  inbounds,
  suggestedQty,
  velocityProvider,
}) {
  return (
    <>
      <EquationContainer>
        <FlexColumn>
          <p>Suggested Quantity</p>
          <span>{suggestedQty} Units</span>
        </FlexColumn>
        <FlexColumn>
          <span>=</span>
        </FlexColumn>
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Carton Rounding</YellowText>
              </p>
            }
          >
            We round suggested quantities to the nearest whole number that is
            evenly divisible by the units per carton property at the warehouse.
          </LightPopup>
        </FlexColumn>
        <MarginLessFlexColumns>
          <span>(</span>
        </MarginLessFlexColumns>
        {minimumBuffer ? (
          <>
            <MarginLessFlexColumns>
              <span>(</span>
            </MarginLessFlexColumns>
            <FlexColumn>
              <LightPopup
                trigger={
                  <p>
                    <YellowText>Minimum Buffer</YellowText>
                  </p>
                }
              >
                This is the minimum desired stock set on the fulfillment
                provider details page.
              </LightPopup>
              <span>{minimumBuffer} Days</span>
            </FlexColumn>
            <FlexColumn>
              <span>+</span>
            </FlexColumn>
          </>
        ) : (
          <></>
        )}
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Lead Time</YellowText>
              </p>
            }
          >
            This is the transit time as well as the inbound/outbound buffers
            while processing the shipment.
          </LightPopup>
          <span>{leadTime} Days</span>
        </FlexColumn>
        {inbounds ? (
          <>
            <FlexColumn>
              <span>{inbounds < 0 ? '-' : '+'}</span>
            </FlexColumn>
            <FlexColumn>
              <LightPopup
                trigger={
                  <p>
                    <YellowText>Existing Inventory</YellowText>
                  </p>
                }
              >
                This accounts for existing inventory and shipments already in
                progress.
              </LightPopup>
              <span>{Math.ceil(Math.abs(inbounds))} Days</span>
            </FlexColumn>
          </>
        ) : (
          <></>
        )}
        {minimumBuffer ? (
          <MarginLessFlexColumns>
            <span>)</span>
          </MarginLessFlexColumns>
        ) : (
          <></>
        )}
        <FlexColumn>
          <span>X</span>
        </FlexColumn>
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Sales Velocity</YellowText>
              </p>
            }
          >
            {brand.name} excludes the Out Of Stock days when considering sales
            velocity.
            <br />
            <br />
            For example: If on a 30-day window of time, there are only 20 days
            in-stock. The 30-days window sales velocity would be the 900 units /
            20 days = 45 units / day.
          </LightPopup>
          <span>{velocityProvider} Units / Day</span>
        </FlexColumn>
        <MarginLessFlexColumns>
          <span>)</span>
        </MarginLessFlexColumns>
      </EquationContainer>
    </>
  );
}

export const tooltTipsText = {
  otherShipments:
    'The originating storage providers in these shipments are different than',
  calcVariables:
    "These are the calculation parameters used in the below suggested quantity equation. The Minimum Desired Stock is dynamic based on the region's primary warehouse where purchase orders are inbounded to.",
  minDesiredStock:
    "The Minimum Desired Stock is dynamic based on the region's primary warehouse where purchase orders are inbounded to.",
  currentStock:
    'Includes all the stock across storage providers and fulfillment centers',
  currentStockGlobal:
    'Includes all the present and reserved (FC Transfer) stock across warehouses and fulfillment centers globally.',
  expected_inventory: 'Includes all inbound shipments in',
  genericSuggestedOrder:
    'This is the suggested purchase order quantity for the respective region. Please view equation below for the detailed breakdown of parameters used for the calculation.',
  leadTime: 'This is the supplier manufacturing lead time.',
  other_fp_credit:
    'This value is the sum of all inventory across fulfillment centers and shipments that are sent from the destination warehouses. It includes buffers used in shipment predictions including minimum desired stock, inbound buffer, and outbound buffers. Value is noted in days.',
  inventory_credits:
    'This value is the sum of all inventory at the primary warehouse itself, and includes any existing open POs that are still not yet completed. Value is in days.',
  other_warehouse_credit:
    'This is the sum of all inventory at all other warehouse providers at the destination region. Value is in days.',
  transitTime:
    'This is estimated transit buffer of shipping the final goods after PO completes.',
  warehouse_buffers:
    'This is the minimum desired inventory configured for the primary warehouse.',
  negativeRawUnits:
    'A negative suggested value implies there is sufficient inventory or inbound shipments to meet buffer requirements.',
  existingInventory:
    "This is the sum of existing inventory at all warehouses and storage providers and any existing PO's and/or future shipments to other providers such as FBA.",
  cartonRounding:
    'We round suggested quantities to the nearest whole number that is evenly divisible by the units per carton property at the warehouse. For purchase order suggestions, we also round up to make sure MOQ is met if the suggested units are below that value.',
  notAllowed:
    'This item has no corresponding supplier. To create Seasonality and Lightning Deals a Supplier must be assigned to the item.',
  salesVelocity: (
    <span>
      {brand.name} excludes the Out Of Stock days when considering sales
      velocity.
      <br />
      <br />
      For example: If on a 30-day window of time, there are only 20 days
      in-stock. The 30-days window sales velocity would be the 900 units / 20
      days = 45 units / day.
    </span>
  ),
  childPredicted: 'The additional units are derived from the sales of sku',
  fpCreditDetails:
    'We consider fulfillment center inventory and shipments only if there are excess units. Because the buffer variables surpass the units available in inventory and shipments, the units at your fulfillment centers are not considered.',
  minZero:
    "Zero Minimum means the smallest number you can have is zero; you can't have less than zero",
  unknownProvider: `There are orders directly fulfilled off ${brand.name} that we cannot attribute to any providers when non-Amazon marketplace are imported.`,
  notEnoughDataForExisting:
    'No purchase orders or shipments found for this item. The system registers it as zero.',
};

export function POProviderPopupContent({
  suggestedQty,
  velocityProvider,
  currentBuffer,
}) {
  const existing_inventory = Math.round(
    currentBuffer?.existing_inventory_credits +
      currentBuffer?.inventory_credits,
  );
  const lead_time = currentBuffer?.lead_time;
  const transit_time =
    currentBuffer?.warehouse_buffer_days + currentBuffer?.transit_time;
  const minBuffer = currentBuffer?.fulfillment_provider_buffer_days;

  return (
    <>
      <EquationContainer className="sk:py-5!">
        <FlexColumn className="sk:items-start!">
          <p>Suggested Quantity</p>
          <span>{suggestedQty} Units</span>
        </FlexColumn>
        <FlexColumn>
          <span>=</span>
        </FlexColumn>
        <FlexColumn>
          <LightPopup trigger={<YellowText>Carton Rounding</YellowText>}>
            {tooltTipsText.cartonRounding}
          </LightPopup>
        </FlexColumn>
        <MarginLessFlexColumns>
          <span>(</span>
        </MarginLessFlexColumns>
        <MarginLessFlexColumns>
          <span>(</span>
        </MarginLessFlexColumns>
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Lead Time</YellowText>
              </p>
            }
          >
            {tooltTipsText.leadTime}
          </LightPopup>
          <span>{lead_time} Days</span>
        </FlexColumn>
        <span>+</span>
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Transit Time</YellowText>
              </p>
            }
          >
            {tooltTipsText.transitTime}
          </LightPopup>
          <span>{transit_time} Days</span>
        </FlexColumn>
        {minBuffer ? (
          <>
            <FlexColumn>
              <span>{minBuffer > 0 ? '+' : '-'}</span>
            </FlexColumn>
            <FlexColumn>
              <LightPopup
                trigger={
                  <p>
                    <YellowText>Minimum Buffer</YellowText>
                  </p>
                }
              >
                {tooltTipsText.warehouse_buffers}
              </LightPopup>
              <span>{Math.abs(minBuffer)} Days</span>
            </FlexColumn>
          </>
        ) : (
          <></>
        )}
        {existing_inventory ? (
          <>
            <FlexColumn>
              <span>{existing_inventory > 0 ? '+' : '-'}</span>
            </FlexColumn>
            <FlexColumn>
              <LightPopup
                trigger={
                  <p>
                    <YellowText>Existing Inventory</YellowText>
                  </p>
                }
              >
                {tooltTipsText.existingInventory}
              </LightPopup>
              <span>{Math.abs(existing_inventory)} Days</span>
            </FlexColumn>
          </>
        ) : (
          <></>
        )}
        <MarginLessFlexColumns>
          <span>)</span>
        </MarginLessFlexColumns>
        <FlexColumn className="sk:mx-2!">
          <span>X</span>
        </FlexColumn>
        <FlexColumn>
          <LightPopup
            trigger={
              <p>
                <YellowText>Sales Velocity</YellowText>
              </p>
            }
          >
            {tooltTipsText.salesVelocity}
          </LightPopup>
          <span>{velocityProvider} Units / Day</span>
        </FlexColumn>
        <MarginLessFlexColumns>
          <span>)</span>
        </MarginLessFlexColumns>
        <FlexColumn className="sk:mx-2!">
          <span>{'>='}</span>
        </FlexColumn>
        <FlexColumn>
          <LightPopup trigger={<YellowText>MOQ</YellowText>}>
            If the value is less than the MOQ, we automatically suggest the MOQ
            to comply with supplier requirements.
          </LightPopup>
        </FlexColumn>
      </EquationContainer>
    </>
  );
}

export function POShipmentPopupContent({ buffer, data }) {
  const { warehouse_outbound_buffer } = buffer;
  const total = warehouse_outbound_buffer || 0;

  return (
    <div>
      {data?.daysLeft && (
        <SpacedRow className="sk:mb-3.5!">
          Shipping Buffer {data.daysLeft}d (Prediction)
        </SpacedRow>
      )}
      {warehouse_outbound_buffer && (
        <SpacedRow className="sk:mb-3.5!">
          <p>Preparation Time</p>
          <span>
            {warehouse_outbound_buffer} Days (
            {Math.round((warehouse_outbound_buffer / total) * 100)}%)
          </span>
        </SpacedRow>
      )}
    </div>
  );
}

export function POGlobalPopupContent({ timeline, suggestedQty }) {
  const filteredRegion = useMemo(() => {
    const arr = [];
    timeline.forEach(region => {
      let qty = 0;

      region.fulfillment_providers.forEach(provider => {
        if (provider.qty > qty) {
          qty = provider.qty;
        }
      });

      if (qty) {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        arr.push(region);
      }
    });
    return arr;
  }, [timeline]);

  return (
    <>
      <EquationContainer>
        <FlexColumn>
          <span>{suggestedQty} Units</span>
        </FlexColumn>
        <FlexColumn>
          <span>=</span>
        </FlexColumn>
        {filteredRegion.map((region, i) => (
          // @ts-expect-error - ts-migration
          <Region key={region.region_name} index={i} data={region} />
        ))}
      </EquationContainer>
      <div className="sk:mt-[1em] sk:max-w-full">
        The total suggested order is the sum of all the regional suggested
        orders
      </div>
    </>
  );
}

function Region({ data, index }) {
  const suggestedQty = useMemo(() => {
    let qty = 0;

    data.fulfillment_providers.forEach(provider => {
      if (provider.qty > qty) {
        qty = provider.qty;
      }
    });

    return qty;
  }, [data]);

  if (!suggestedQty) {
    return <></>;
  }

  return (
    <>
      {index !== 0 && (
        <FlexColumn>
          <span>+</span>
        </FlexColumn>
      )}
      <div className="sk:flex! sk:items-center!">
        <span className="sk:mr-1!">{suggestedQty}</span>
        <span className="sk:mr-1!">Units</span>
        <span>({data.region_name})</span>
      </div>
    </>
  );
}
